/* eslint-disable @typescript-eslint/no-floating-promises */
import React, { useMemo, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { TbFileExport, TbTags, TbTrash } from 'react-icons/tb';

import {
  ApolloError,
  Divider,
  Heading,
  HStack,
  Icon,
  IconButton,
  LoadingIndicator,
  NavPersistedScrollPane as ScrollPane,
  SearchInput,
  Spacer,
  Text,
  Tooltip,
  useShowMessage,
  VStack,
} from '../../core-ui';
import { useNavigation, useNavPersistedState } from '../Navigation';
import { useSeason } from '../SeasonState';
import { APP_STATUSES } from '../../support/constants/AppStatuses';
import { confirm } from '../App/AppDialog';
import { useShowApolloError } from '../../support/graphql/useShowApolloError';

import { VendorListView } from './VendorListView';
import { ExportDialog } from './ExportDialog';
import { TagManagerDialog } from './TagManagerDialog';
import { FilterDropdown } from './FilterDropdown';
import { BulkStatusChangeDropdown } from './BulkStatusChangeDropdown';
import { BulkTagChangeDropdown } from './BulkTagChangeDropdown';
import {
  GET_VENDOR_LIST,
  VENDOR_BULK_DELETE,
  VENDOR_BULK_UPDATE,
} from './graphql';
import {
  GetVendorList,
  GetVendorListVariables,
} from './__generated__/GetVendorList';
import {
  VendorBulkDelete,
  VendorBulkDeleteVariables,
} from './__generated__/VendorBulkDelete';
import {
  VendorBulkUpdate,
  VendorBulkUpdateVariables,
} from './__generated__/VendorBulkUpdate';
import { filterVendors } from './filterVendors';
import { getDefaultVendorStatusSelection } from './getDefaultVendorStatusSelection';
import MarketDatesFilterModal from '../Mailer/MarketDatesFilterModal';
import { MarketDates } from '../../runtypes/VendorRuntype';
import { getCategoryOptionsFromGraphQLFields } from '../../support/helpers/CategoryOptions';
import { toMap } from '../../support/helpers/toMap';

export function VendorListPage() {
  const { navigate } = useNavigation();
  const { season } = useSeason();
  const showMessage = useShowMessage();
  const showApolloError = useShowApolloError();

  const [selected, setSelected] = useNavPersistedState(
    'selected-vendors',
    new Set<string>(),
  );

  const [showExportDialog, setShowExportDialog] = useState(false);
  const [showTagsDialog, setShowTagsDialog] = useState(false);

  const [searchText, setSearchText] = useNavPersistedState('search-value', '');
  const [selectedCategories, setSelectedCategories] = useNavPersistedState(
    'selected-categories',
    new Set<string>(),
  );
  const [selectedStatuses, setSelectedStatuses] = useNavPersistedState(
    'selected-statuses',
    getDefaultVendorStatusSelection(),
  );
  const [
    selectedMarketDates,
    setSelectedMarketDates,
  ] = useNavPersistedState<MarketDates>('selected-market-dates', {});
  const [selectedTags, setSelectedTags] = useNavPersistedState(
    'selected-tags',
    new Set<string>(),
  );

  const { error, data, refetch } = useQuery<
    GetVendorList,
    GetVendorListVariables
  >(GET_VENDOR_LIST, {
    variables: { seasonId: season?.id ?? '' },
    fetchPolicy: 'cache-and-network',
  });

  const allVendors = useMemo(() => data?.season?.vendors ?? [], [
    data?.season?.vendors,
  ]);
  const totalVendors = allVendors.length;

  const markets = data?.season?.markets ?? [];
  const tags = data?.season?.tags ?? [];
  const vendorCategories = data?.season?.vendorCategories ?? [];
  const options = getCategoryOptionsFromGraphQLFields(
    data?.season?.signupForm?.fields ?? [],
  );
  const optionsMap = toMap(options);

  const filteredVendors = useMemo(() => {
    return filterVendors({
      vendors: allVendors,
      filters: {
        searchText,
        selectedCategories,
        selectedStatuses,
        selectedTags,
        selectedMarketDates,
      },
      optionsMap,
    });
  }, [
    allVendors,
    optionsMap,
    searchText,
    selectedCategories,
    selectedMarketDates,
    selectedStatuses,
    selectedTags,
  ]);

  const [vendorBulkDelete] = useMutation<
    VendorBulkDelete,
    VendorBulkDeleteVariables
  >(VENDOR_BULK_DELETE, {
    onError: showApolloError,
    onCompleted: () => {
      setSelected(new Set());
    },
    refetchQueries: [GET_VENDOR_LIST],
  });

  const [vendorBulkUpdate] = useMutation<
    VendorBulkUpdate,
    VendorBulkUpdateVariables
  >(VENDOR_BULK_UPDATE, {
    onError: showApolloError,
    refetchQueries: [GET_VENDOR_LIST],
  });

  const deleteVendors = async (selected: Set<string>) => {
    const proceed = await confirm(
      t('Delete Vendors?'),
      t('Are you sure you want to permanently delete {count} vendor(s)?', {
        count: selected.size,
      }),
    );
    if (!proceed) {
      return;
    }
    await vendorBulkDelete({
      variables: { ids: Array.from(selected) },
    });
    showMessage(
      'success',
      t('Successfully deleted {count} vendor(s)', {
        count: selected.size,
      }),
    );
  };

  if (error) {
    return <ApolloError error={error} />;
  }

  const isLoading = !data;

  return (
    <VStack px={10} pt={10} flex={1} minH={0} alignItems="stretch" spacing={2}>
      <HStack>
        <Heading as="h2" size="lg">
          {t('Vendors')}
        </Heading>
      </HStack>
      <Divider borderColor="gray.200" />
      {selected.size === 0 ? (
        <HStack>
          <SearchInput
            value={searchText}
            onChange={(event) => {
              setSearchText(event.target.value);
            }}
            onClear={() => {
              setSearchText('');
            }}
          />
          <FilterDropdown
            categories={vendorCategories}
            selectedCategories={selectedCategories}
            statuses={APP_STATUSES}
            selectedStatuses={selectedStatuses}
            tags={tags}
            selectedTags={selectedTags}
            onApply={({ categories, statuses, tags }) => {
              setSelectedCategories(categories);
              setSelectedStatuses(statuses);
              setSelectedTags(tags);
            }}
            onReset={() => {
              setSelectedCategories(new Set());
              setSelectedStatuses(getDefaultVendorStatusSelection());
              setSelectedTags(new Set());
            }}
          />
          <MarketDatesFilterModal
            markets={markets}
            initialSelection={selectedMarketDates}
            onSubmit={(value) => setSelectedMarketDates(value)}
          />
          {filteredVendors.length === totalVendors ? null : (
            <Text>
              {t('Showing {showing} of {total} vendor(s)', {
                showing: filteredVendors.length,
                total: totalVendors,
              })}
            </Text>
          )}
          <Spacer />
          <Tooltip label={t('Manage Tags')} placement="top">
            <IconButton
              aria-label={t('Manage Tags')}
              icon={<Icon as={TbTags} fontSize={20} />}
              onClick={() => setShowTagsDialog(true)}
            />
          </Tooltip>
          <Tooltip label={t('Export')} placement="top">
            <IconButton
              aria-label={t('Export')}
              icon={<Icon as={TbFileExport} fontSize={20} />}
              onClick={() => setShowExportDialog(true)}
            />
          </Tooltip>
        </HStack>
      ) : (
        // Note: the height here is Chakra's default height for a text input
        <HStack minH={10}>
          <Text>
            {t('{count} vendor(s) selected', { count: selected.size })}
          </Text>
          <Spacer />
          <BulkStatusChangeDropdown
            onStatusSelect={async (status) => {
              if (status === 'DECLINED') {
                const proceed = await confirm(
                  t('Are you sure?'),
                  t(
                    'This will mark selected vendors as declined and send an email to each one (if configured).',
                  ),
                );
                if (!proceed) {
                  return;
                }
              }
              const ids = Array.from(selected);
              vendorBulkUpdate({
                variables: { ids, updates: { status } },
              });
              setSelected(new Set<string>());
            }}
          />
          <BulkTagChangeDropdown
            tags={tags}
            onTagSelect={(tag) => {
              const vendorsWithTag = new Set<string>();
              const vendorsWithoutTag = new Set<string>();
              for (const vendor of allVendors) {
                if (selected.has(vendor.id)) {
                  const hasTag = vendor.tags.some(({ id }) => id === tag.id);
                  if (hasTag) {
                    vendorsWithTag.add(vendor.id);
                  } else {
                    vendorsWithoutTag.add(vendor.id);
                  }
                }
              }
              if (vendorsWithTag.size) {
                const ids = Array.from(vendorsWithTag);
                vendorBulkUpdate({
                  variables: { ids, updates: { removeTags: [tag.id] } },
                });
              } else {
                const ids = Array.from(vendorsWithoutTag);
                vendorBulkUpdate({
                  variables: { ids, updates: { addTags: [tag.id] } },
                });
              }
            }}
          />
          <Tooltip label={t('Delete')} placement="top">
            <IconButton
              variant="ghost"
              aria-label={t('Delete')}
              icon={<Icon as={TbTrash} fontSize={20} />}
              onClick={() => deleteVendors(selected)}
            />
          </Tooltip>
        </HStack>
      )}
      <ScrollPane
        storeKey="vendor-list"
        contentContainerProps={{ flexGrow: 1, pb: 10 }}
      >
        {isLoading ? (
          <LoadingIndicator />
        ) : (
          <VendorListView
            optionsMap={optionsMap}
            vendors={filteredVendors}
            tags={tags}
            selected={selected}
            onSelectionChange={(selected) => setSelected(selected)}
            onVendorClick={(vendor) => {
              navigate(`/vendors/:id`, { id: vendor.id });
            }}
          />
        )}
      </ScrollPane>
      <TagManagerDialog
        isOpen={showTagsDialog}
        onClose={() => setShowTagsDialog(false)}
        onChangeSaved={() => {
          // TODO: Refetch only tags, not vendors
          refetch();
        }}
      />
      <ExportDialog
        isOpen={showExportDialog}
        onClose={() => setShowExportDialog(false)}
      />
    </VStack>
  );
}
